<script lang="ts">
  import { getPalette, currentTheme } from '../../theme.svelte';
  import { navigate } from '../../route.svelte';
  import { currentUser } from '../../auth.svelte';
  import { PLANS_URL } from '../../externalUrls';

  let {
    onToggleSidebar,
    isSidebarCollapsed = false,
    isMobile = false,
    palette = getPalette(),
    heading = ''
  } = $props();

  function mainIconClass() {
    if (!isMobile) {
      return 'bi bi-layout-sidebar';
    }
    // móvil
    return isSidebarCollapsed ? 'bi bi-list' : 'bi bi-x-square';
  }

  function mainTitle() {
    if (!isMobile) return 'Mostrar / ocultar barra lateral';
    return isSidebarCollapsed ? 'Abrir menú' : 'Cerrar menú';
  }

  // ── Créditos ─────────────────────────────────────────────
  // Semáforo discreto: el saldo vira a ámbar, naranja y rojo conforme se
  // acerca a 0. `null` = aún no se conoce el saldo (no se pinta nada).
  let remaining = $derived(currentUser.availableMessages);

  function creditsColor(value: number | null): string {
    if (value === null) return '';
    const dark = currentTheme.value === 'dark';
    if (value <= 0) return dark ? '#f87171' : '#b91c1c';
    if (value <= 2) return dark ? '#fb923c' : '#c2410c';
    if (value <= 5) return dark ? '#fbbf24' : '#b45309';
    return '';
  }

  let creditsTone = $derived(creditsColor(remaining));
  let creditsStyle = $derived(
    creditsTone
      ? `color: ${creditsTone}; ` +
        `border-color: color-mix(in srgb, ${creditsTone} 38%, transparent); ` +
        `background-color: color-mix(in srgb, ${creditsTone} 9%, transparent);`
      : `color: ${palette.textSecondary}; border-color: ${palette.borderSoft}; background-color: transparent;`
  );
  let creditsLabel = $derived(remaining === 1 ? 'crédito' : 'créditos');
  let creditsTitle = $derived(
    remaining === 0
      ? 'Sin créditos disponibles'
      : `${remaining} ${creditsLabel} disponible${remaining === 1 ? '' : 's'}`
  );
</script>

<div class="header">
  <div class="left">
    <button
      class="square-icon {mainIconClass()}"
      type="button"
      onclick={onToggleSidebar}
      title={mainTitle()}
      style={`color: ${palette.icon}; border-color: ${palette.borderSoft};`}
    ></button>

    {#if isSidebarCollapsed && !isMobile}
      <!-- Solo en desktop cuando la barra está colapsada -->
      <button
        class="square-icon bi bi-plus-lg"
        type="button"
        title="Nuevo chat"
        onclick={() => navigate('/')}
        style={`color: ${palette.icon}; border-color: ${palette.borderSoft};`}
      ></button>
    {/if}
  </div>
  {#if heading}
    <h1 class="heading" style={`color: ${palette.textPrimary};`}>{heading}</h1>
  {/if}
  <div class="right">
    {#if remaining !== null}
      <div class="credits" style={creditsStyle} title={creditsTitle}>
        <span class="credits-count" aria-live="polite">{remaining}</span>
        <span class="credits-label">{creditsLabel}</span>
        <a
          class="credits-buy"
          href={PLANS_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Comprar más créditos"
          aria-label="Comprar más créditos"
        >
          <i class="bi bi-plus-lg" aria-hidden="true"></i>
        </a>
      </div>
    {/if}
  </div>
</div>

<style>
  .header {
    height: 48px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px 0 16px;
  }

  .left {
    display: flex;
    gap: 6px;
  }

  .square-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid;
    background: transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .right {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  /* ── Indicador de créditos ───────────────────────────────
     Píldora mínima: número + etiqueta + acceso a compra. El color sólo
     cambia cuando el saldo se acerca a 0. */
  .credits {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 28px;
    padding: 0 4px 0 10px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    user-select: none;
    transition: color 180ms ease, background-color 180ms ease, border-color 180ms ease;
  }

  .credits-count {
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }

  .credits-label {
    font-size: 11px;
    opacity: 0.8;
  }

  .credits-buy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: inherit;
    background: color-mix(in srgb, currentColor 12%, transparent);
    text-decoration: none;
    transition: background-color 120ms ease;
  }

  .credits-buy:hover {
    background: color-mix(in srgb, currentColor 24%, transparent);
  }

  .credits-buy:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 1px;
  }

  .credits-buy i {
    font-size: 11px;
    line-height: 1;
  }

  .heading {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.25;
  }

  @media (max-width: 768px) {
    .header {
      height: calc(48px + env(safe-area-inset-top));
      padding: calc(8px + env(safe-area-inset-top)) 8px 0 8px;
    }
  }

  /* En pantallas muy estrechas el número y el botón bastan. */
  @media (max-width: 480px) {
    .credits-label {
      /* Oculta visualmente, pero conserva la unidad para lectores de pantalla. */
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    }

    .credits {
      padding-left: 8px;
    }
  }

  /* El cambio de color es informativo, no movimiento: se puede desactivar. */
  @media (prefers-reduced-motion: reduce) {
    .credits,
    .credits-buy {
      transition: none;
    }
  }
</style>
